import AngleRightIcon from '@ui-components-3/icons/lib/regular/AngleRight'
import { ForwardedRef, forwardRef, useCallback } from 'react'
import AppleIcon from '@ui-components-3/icons/lib/brands/Apple'
import DownloadIcon from '@ui-components-3/icons/lib/solid/Download'
import GoogleCalendarIcon from 'assets/images/brandLogos/ic_google_calendar.png'
import Office365Icon from 'assets/images/brandLogos/ic_office_365.png'
import YahooIcon from 'assets/images/brandLogos/ic_yahoo.png'
import { google, office365, yahoo, ics, CalendarEvent } from 'calendar-link'
import Modal, { ModalRef } from '@ui-components-3/ui/lib/components/Modal'
import PendingVisit from 'types/visit/pendingVisit'
import { DisplayVisitType } from 'util/visit-types'
import ExternalLink from 'components/common/ExternalLink'
import { useDispatch } from 'react-redux'
import { postPendingVisitCalendarInfo } from 'redux/thunks/visits'
import { CalendarVisit } from 'hooks/useCalendarInfo'

type AddToCalendarModalProps = {
  closeModal: () => void
  pendingVisit: PendingVisit
  getCalendarInfo: () => Promise<void>
}

const adjustScheduledDate = (scheduledDate: string | Date): Date => {
  // This is necessary because the fn yahoo() formats the date to the time zone automatically and Yahoo Calendar does it again.
  const date = new Date(scheduledDate)
  const timezoneOffset = date.getTimezoneOffset()

  return new Date(date.getTime() - timezoneOffset * 60 * 1000)
}

const AddToCalendarModal = forwardRef(
  ({ closeModal, pendingVisit, getCalendarInfo }: AddToCalendarModalProps, ref: ForwardedRef<ModalRef>) => {
    const dispatch = useDispatch()
    const event: CalendarEvent = {
      title: `TimelyCare Visit - ${DisplayVisitType[pendingVisit.visitType]}`,
      description: `With ${pendingVisit.providerName}`,
      start: pendingVisit.scheduledAt,
      duration: [Number(pendingVisit.scheduledDuration), 'minutes'],
      location: pendingVisit.location,
      allDay: false,
    }

    const downloadICS = () => {
      const icsLink = ics(event)
      const element = document.createElement('a')
      element.setAttribute('href', icsLink)
      element.setAttribute('download', 'timelyCareVisit.ics')
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    }

    const saveCalendarInfo = useCallback(
      async (type: string) => {
        await dispatch(postPendingVisitCalendarInfo(pendingVisit.memberId, pendingVisit.pendingVisitId, type))
        if (getCalendarInfo) {
          await getCalendarInfo()
        }
        closeModal()
      },
      [closeModal, dispatch, getCalendarInfo, pendingVisit.memberId, pendingVisit.pendingVisitId],
    )

    const calendarOptions = [
      {
        label: 'Apple',
        icon: <AppleIcon aria-hidden="true" className="h-8 w-8 text-neutral-800" />,
        action: downloadICS,
        type: 'other',
      },
      {
        label: 'Google',
        icon: <img src={GoogleCalendarIcon} alt="" />,
        link: google(event),
        type: 'google',
      },
      {
        label: 'Office 365',
        icon: <img src={Office365Icon} alt="" />,
        link: office365(event),
        type: 'office365',
      },
      {
        label: 'Yahoo',
        icon: <img src={YahooIcon} alt="" />,
        link: yahoo({ ...event, start: adjustScheduledDate(pendingVisit.scheduledAt) }),
        type: 'yahoo',
      },
      {
        label: 'Download Calendar Event',
        icon: <DownloadIcon aria-hidden="true" className="text-primary-700 h-6 w-6" />,
        action: downloadICS,
        type: 'other',
      },
    ]

    const cancelBtn = (
      <div className="flex items-end justify-end">
        <button className="btn btn-neutral-outlined" onClick={closeModal} type="button">
          Cancel
        </button>
      </div>
    )

    return (
      <Modal footer={cancelBtn} label="Add to Calendar" size="m" ref={ref} onClose={closeModal}>
        <p className="typography-body-xl mb-4 text-neutral-800">
          Selecting a calendar platform will redirect you to the calendar event in a new tab.
        </p>
        <div className="flex flex-col gap-2">
          {calendarOptions.map((option) =>
            option.link ? (
              <ExternalLink
                key={option.label}
                href={option.link}
                customConfirmation={() => {
                  saveCalendarInfo(option.type)
                }}
              >
                <div className="flex h-[56px] items-center justify-between rounded-xl border border-neutral-300 bg-white px-4">
                  <div className="flex items-center">
                    <div>{option.icon}</div>
                    <h5 className="typography-h5 ml-4 font-semibold text-neutral-800">{option.label}</h5>
                  </div>
                  <AngleRightIcon className="text-primary-700 h-5 w-5" aria-hidden="true" />
                </div>
              </ExternalLink>
            ) : (
              <button
                key={option.label}
                onClick={() => {
                  option.action()
                  saveCalendarInfo(option.type)
                }}
                className="flex h-[56px] items-center justify-between rounded-xl border border-neutral-300 bg-white px-4"
                type="button"
              >
                <div className="flex items-center">
                  <div>{option.icon}</div>
                  <h5 className="typography-h5 ml-4 font-semibold text-neutral-800">{option.label}</h5>
                </div>
                <AngleRightIcon className="text-primary-700 h-5 w-5" aria-hidden="true" />
              </button>
            ),
          )}
        </div>
      </Modal>
    )
  },
)

export default AddToCalendarModal
